/**
 * CareEditForm — Formular zum Bearbeiten einer bestehenden Betreuung.
 *
 * Wiederverwendbar in:
 *   - EditDrawer (Dashboard-Kompakt-Widgets + BetreuungenTab Tabellenzeile)
 *
 * Felder: BK-Auswahl, Startdatum, Enddatum, Status, Benachrichtigen und, im
 * PB-Profil bei „Bestätigt" mit Enddatum, die Checkbox aus CRM 1.0 „Das neue
 * Startdatum auf den <Enddatum> verlegen" (standardmäßig an).
 * Speichern via v2Cares.update(id, payload) — PUT /v2/admin/cares/{id} mit
 * optimistischem Locking: `updated_at` des geladenen Einsatzes wird
 * mitgeschickt; hat jemand anderes inzwischen gespeichert, antwortet die API
 * mit 409 und das Formular bietet den aktuellen Stand an (nichts wird
 * stillschweigend überschrieben).
 * enddatum ist Pflicht bei status ∈ {0, 2}.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, getThemeColors } from '../../../lib/theme';
import type { V1Care } from '../../../lib/v1';
import { v2Cares, type V2Care } from '../../../lib/cares';
import { toStaleWriteError } from '../../../lib/optimisticLock';
import { toCareOverlap, type CareOverlapInfo } from '../../../lib/careOverlap';
import { CareOverlapWarning } from '../../../components/care/CareOverlapWarning';


import { BkPickerField } from './BkPickerField';
import { DateInput } from '../../../components/DateInput';
import { StartdatumAutoHint } from '../../../components/care/StartdatumAutoHint';

/** Status „Bestätigt" — dann setzt die API das PB-Startdatum auf das Enddatum. */
const CARE_STATUS_CONFIRMED = 2;

/** Status „Vorbereitung" — nur in diesem Status ist eine Betreuung löschbar. */
export const CARE_STATUS_VORBEREITUNG = 3;

/** Fehlertext aus dem v2-Envelope (`errors[0].message`) oder Fallback. */
function v2ErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } })?.response?.data;
  return data?.errors?.[0]?.message ?? data?.message ?? fallback;
}

/* ─── Status-Optionen ────────────────────────────────────────────────────── */

const CARE_STATUS_OPTIONS: { value: number; labelKey: string }[] = [
  { value: 3, labelKey: 'pflege.cares.status_options.3' },
  { value: 0, labelKey: 'pflege.cares.status_options.0' },
  { value: 2, labelKey: 'pflege.cares.status_options.2' },
  { value: 1, labelKey: 'pflege.cares.status_options.1' },
];

/* ─── Props ──────────────────────────────────────────────────────────────── */

export interface CareEditFormProps {
  dark: boolean;
  care: V1Care & { bk_name?: string | null; pb_user_id: number };
  onSaved: (updated: V1Care) => void;
  onCancel: () => void;
  /**
   * Nach dem Löschen der Betreuung (nur im Status „Vorbereitung" möglich).
   * Ohne eigenen Handler wird `onSaved` aufgerufen, damit die Liste neu lädt.
   */
  onDeleted?: (careId: number) => void;
  /**
   * Die API hat das Startdatum des PB automatisch fortgeschrieben — neuer
   * Wert als YYYY-MM-DD (z. B. um die Sidebar des PB-Profils nachzuziehen).
   */
  onPbStartdatumChanged?: (startdatum: string) => void;
}

export function CareEditForm({ dark, care, onSaved, onCancel, onDeleted, onPbStartdatumChanged }: CareEditFormProps) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();

  const [bkId, setBkId] = useState<number | null>(care.bk_user_id ?? null);
  // Anzeigename der gewählten BK — Fallback für den Picker, falls die aktuell
  // zugewiesene BK nicht in der Matching-Liste steht.
  const [bkName, setBkName] = useState<string | null>(care.bk_name ?? null);
  const [startdatum, setStartdatum] = useState(care.startdatum?.slice(0, 10) ?? '');
  const [enddatum, setEnddatum] = useState(care.enddatum?.slice(0, 10) ?? '');
  const [status, setStatus] = useState<number>(care.status ?? 3);
  const [notify, setNotify] = useState(false);
  // Batch-12: gespeichertes Flag (kein automatischer Versand).
  const [pflegedienstBenachrichtigen, setPflegedienstBenachrichtigen] = useState(
    Boolean((care as Record<string, unknown>).pflegedienst_benachrichtigen),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Optimistisches Locking (siehe src/lib/optimisticLock.ts).
  const [lockStamp, setLockStamp] = useState<string | null>(care.updated_at ?? null);
  const [conflict, setConflict] = useState<V2Care | null>(null);
  // Überlappung > 1 Tag mit einer anderen Betreuung: Warnung, nächster Klick speichert trotzdem.
  const [overlap, setOverlap] = useState<CareOverlapInfo | null>(null);

  const endRequired = status === 0 || status === 2;

  // Löschen: nur solange die GESPEICHERTE Betreuung in Vorbereitung ist
  // (nicht der im Formular gewählte, noch ungespeicherte Status).
  const deletable = care.status === CARE_STATUS_VORBEREITUNG;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await v2Cares.delete(care.id);
      if (onDeleted) onDeleted(care.id);
      else onSaved(care);
    } catch (err) {
      setError(v2ErrorMessage(err, t('pflege.cares.delete_error')));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }, [care, deleting, onDeleted, onSaved, t]);

  /** Konflikt: aktuellen Server-Stand in die Felder übernehmen. */
  const adoptCurrent = useCallback(() => {
    if (!conflict) return;
    setBkId(conflict.bk_user_id ?? null);
    setBkName(conflict.bk_name ?? null);
    setStartdatum(conflict.startdatum?.slice(0, 10) ?? '');
    setEnddatum(conflict.enddatum?.slice(0, 10) ?? '');
    setStatus(conflict.status ?? 3);
    setPflegedienstBenachrichtigen(Boolean((conflict as Record<string, unknown>).pflegedienst_benachrichtigen));
    setLockStamp(conflict.updated_at ?? null);
    setConflict(null);
    setError(null);
  }, [conflict]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!bkId) { setError(t('pflege.cares.create_bk_required')); return; }
    if (!startdatum) { setError(t('pflege.cares.create_startdatum_required')); return; }
    if (endRequired && !enddatum) { setError(t('pflege.cares.create_enddatum_required')); return; }
    setSaving(true);
    try {
      const { care: updated, startdatum: pbStartdatum } = await v2Cares.update(care.id, {
        bk_user_id: bkId,
        pb_user_id: care.pb_user_id,
        startdatum,
        enddatum: enddatum || null,
        status,
        notify,
        pflegedienst_benachrichtigen: pflegedienstBenachrichtigen,
        ...(overlap ? { confirm_overlap: true } : {}),
        ...(lockStamp ? { updated_at: lockStamp } : {}),
      });
      setConflict(null);
      setOverlap(null);
      setLockStamp(updated.updated_at ?? null);
      // Bestätigt → die API hat das PB-Startdatum automatisch auf das Enddatum verlegt.
      // War die Betreuung bestätigt und ist es jetzt nicht mehr (storniert
      // oder zurückgestuft), hat die API es auf die verbleibende bestätigte
      // Betreuung zurückgesetzt — auch das an das Profil melden.
      if ((status === CARE_STATUS_CONFIRMED || care.status === CARE_STATUS_CONFIRMED) && pbStartdatum) {
        onPbStartdatumChanged?.(String(pbStartdatum).slice(0, 10));
      }
      onSaved(updated);
    } catch (err) {
      const stale = toStaleWriteError<V2Care>(err);
      if (stale) {
        setConflict(stale.current);
        setError(t('common.conflict.stale_write'));
        return;
      }
      const needsConfirm = toCareOverlap(err);
      if (needsConfirm) {
        setOverlap(needsConfirm);
        return;
      }
      const apiErr = err as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } };
      const msg = apiErr.response?.data?.errors?.[0]?.message
        ?? apiErr.response?.data?.message
        ?? (err instanceof Error ? err.message : null);
      setError(msg || t('pflege.detail.dashboard.save_error'));
    } finally {
      setSaving(false);
    }
  }, [bkId, startdatum, enddatum, status, notify, pflegedienstBenachrichtigen, care, endRequired, t, onSaved, onPbStartdatumChanged, lockStamp, overlap]);

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '8px 12px', width: '100%', boxSizing: 'border-box',
    border: `1px solid ${tc.border}`, borderRadius: 8, background: tc.inputBg,
    color: tc.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: tc.muted, display: 'block', marginBottom: 5,
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* BK picker */}
      <div>
        <label style={labelStyle}>{t('pflege.cares.create_bk_label')}</label>
        <BkPickerField
          dark={dark}
          pbUserId={care.pb_user_id}
          value={bkId}
          valueLabel={bkName}
          onChange={(id, name) => { setBkId(id); setBkName(name); }}
        />
      </div>

      {/* Startdatum */}
      <div>
        <label style={labelStyle}>{t('pflege.cares.create_startdatum_label')}</label>
        <DateInput
          ariaLabel={t('pflege.cares.create_startdatum_label')}
          value={startdatum}
          onChange={v => { setStartdatum(v); setOverlap(null); }}
          style={inputStyle}
        />
      </div>

      {/* Enddatum */}
      <div>
        <label style={labelStyle}>
          {t('pflege.cares.create_enddatum_label')}
          {!endRequired && <span style={{ fontWeight: 400, color: tc.muted }}> (optional)</span>}
        </label>
        <DateInput
          ariaLabel={t('pflege.cares.create_enddatum_label')}
          value={enddatum}
          onChange={v => { setEnddatum(v); setOverlap(null); }}
          style={inputStyle}
        />
      </div>

      {/* Status */}
      <div>
        <label style={labelStyle}>{t('pflege.cares.create_status_label')}</label>
        <select
          value={status}
          onChange={e => setStatus(Number(e.target.value))}
          style={inputStyle}
        >
          {CARE_STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </select>
      </div>

      {/* Checkboxen */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: tc.text, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={notify}
          onChange={e => setNotify(e.target.checked)}
          style={{ cursor: 'pointer', accentColor: BRAND, width: 15, height: 15 }}
        />
        {t('pflege.cares.create_notify_label')}
      </label>
      <StartdatumAutoHint dark={dark} status={status} enddatum={enddatum} />
      {/* Bestätigte Betreuung wird storniert/zurückgestuft → Startdatum des PB wird aktualisiert. */}
      {care.status === CARE_STATUS_CONFIRMED && status !== CARE_STATUS_CONFIRMED && (
        <div data-testid="care-startdatum-cancel-hint" style={{ fontSize: 12, color: tc.muted, lineHeight: 1.4 }}>
          {t('pflege.cares.startdatum_cancel_hint')}
        </div>
      )}
      {/* Batch-12: Flag „Pflegedienst benachrichtigen" (nur Speicherung) */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: tc.text, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={pflegedienstBenachrichtigen}
          onChange={e => setPflegedienstBenachrichtigen(e.target.checked)}
          style={{ cursor: 'pointer', accentColor: BRAND, width: 15, height: 15 }}
        />
        {t('pflege.cares.pflegedienst_benachrichtigen_label')}
      </label>

      {overlap && <CareOverlapWarning overlap={overlap} dark={dark} />}

      {error && (
        <div role="alert" style={{ padding: '8px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 7, fontSize: 12.5, color: '#b42318', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>{error}</span>
          {conflict && (
            <button
              type="button"
              onClick={adoptCurrent}
              style={{
                fontSize: 12, fontWeight: 600, padding: '6px 10px', background: 'none',
                border: `1px solid ${tc.border}`, borderRadius: 7, color: tc.text,
                cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
              }}
            >
              {t('common.conflict.reload')}
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button
          type="submit"
          disabled={saving}
          style={{
            flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff',
            border: 'none', borderRadius: 8, padding: '10px 0',
            cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1,
            fontFamily: 'Outfit,sans-serif',
          }}
        >
          {saving ? '…' : overlap ? t('pflege.cares.overlap.save_anyway') : t('common.action.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            fontSize: 13, padding: '10px 18px', background: 'none',
            border: `1px solid ${tc.border}`, borderRadius: 8, color: tc.muted,
            cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
          }}
        >
          {t('common.action.cancel')}
        </button>
      </div>

      {/* Löschen — nur im Status „Vorbereitung" */}
      {deletable && (
        <div style={{ borderTop: `1px solid ${tc.border}`, paddingTop: 12, marginTop: 2 }}>
          {confirmDelete ? (
            <div data-testid="care-delete-confirm" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ flex: 1, fontSize: 12.5, color: tc.text }}>{t('pflege.cares.delete_confirm')}</span>
              <button
                type="button"
                data-testid="care-delete-yes"
                disabled={deleting}
                onClick={handleDelete}
                style={{
                  fontSize: 13, fontWeight: 600, padding: '8px 14px', background: '#b42318', color: '#fff',
                  border: 'none', borderRadius: 8, cursor: deleting ? 'wait' : 'pointer', opacity: deleting ? 0.7 : 1,
                  fontFamily: 'Outfit,sans-serif',
                }}
              >
                {deleting ? '…' : t('common.action.delete')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                style={{
                  fontSize: 13, padding: '8px 14px', background: 'none', border: `1px solid ${tc.border}`,
                  borderRadius: 8, color: tc.muted, cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
                }}
              >
                {t('common.action.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="care-delete"
              onClick={() => setConfirmDelete(true)}
              style={{
                fontSize: 13, fontWeight: 600, padding: '8px 14px', background: dark ? '#1a0505' : '#fff5f5',
                border: '1px solid #fda29b', borderRadius: 8, color: '#b42318', cursor: 'pointer',
                fontFamily: 'Outfit,sans-serif',
              }}
            >
              {t('pflege.cares.delete')}
            </button>
          )}
        </div>
      )}
    </form>
  );
}
